/**
 * Authentication context and hook
 * @module features/auth/hooks/useAuth
 */

import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { AuthContextType } from '../types/auth.types';
import { getConfig } from '../../../core/config/environment';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_SESSION_KEYS = {
  token: 'auth_token',
  role: 'auth_role',
  extraRoles: 'auth_extra_roles',
  displayName: 'auth_display_name',
  avatarUrl: 'auth_avatar_url',
  userId: 'auth_user_id',
  adminId: 'auth_admin_id',
} as const;

const clearLegacyAuthStorage = (): void => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('displayName');
  } catch (e) {
    // Ignore storage cleanup errors in restricted browser contexts.
  }
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);
  const [userId, setUserId] = useState<number | null>(null);
  const [adminId, setAdminId] = useState<number | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [extraRoles, setExtraRoles] = useState<string[]>([]);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const hydrateAuth = async () => {
      clearLegacyAuthStorage();
      const sessionToken = sessionStorage.getItem(AUTH_SESSION_KEYS.token);

      try {
        const baseUrl = getConfig().API_BASE_URL;
        const response = await fetch(`${baseUrl}/api/account`, {
          method: 'GET',
          credentials: 'include',
          headers: sessionToken
            ? {
              Authorization: `Bearer ${sessionToken}`,
            }
            : undefined,
        });

        if (!response.ok) {
          setIsAuthenticated(false);
          setUserId(null);
          setRole(null);
          setDisplayName(null);
          setAvatarUrl(null);
          setToken(null);
          setIsAuthLoading(false);
          return;
        }

        const payload = await response.json();
        if (payload?.EC === 0 && payload?.DT?.access_token) {
          const nextToken = payload?.DT?.access_token || sessionToken;
          const nextUserId = payload?.DT?.userId ? Number(payload.DT.userId) : null;
          const nextAdminId = payload?.DT?.adminId != null ? Number(payload.DT.adminId) : null;
          const nextRole = payload?.DT?.groupWithRoles?.name || 'User';
          const nextExtraRoles: string[] = Array.isArray(payload?.DT?.extraRoles)
            ? payload.DT.extraRoles.map((r: { name: string }) => r.name)
            : [];
          const nextDisplayName = payload?.DT?.username || null;
          const nextAvatarUrl = payload?.DT?.avatarUrl || null;

          setIsAuthenticated(true);
          setUserId(nextUserId);
          setAdminId(nextAdminId);
          setRole(nextRole);
          setExtraRoles(nextExtraRoles);
          setDisplayName(nextDisplayName);
          setAvatarUrl(nextAvatarUrl);
          setToken(nextToken);
          if (nextToken) {
            sessionStorage.setItem(AUTH_SESSION_KEYS.token, nextToken);
          }
          if (nextUserId != null) {
            sessionStorage.setItem(AUTH_SESSION_KEYS.userId, String(nextUserId));
          } else {
            sessionStorage.removeItem(AUTH_SESSION_KEYS.userId);
          }
          if (nextAdminId != null) {
            sessionStorage.setItem(AUTH_SESSION_KEYS.adminId, String(nextAdminId));
          } else {
            sessionStorage.removeItem(AUTH_SESSION_KEYS.adminId);
          }
          sessionStorage.setItem(AUTH_SESSION_KEYS.role, nextRole);
          sessionStorage.setItem(AUTH_SESSION_KEYS.extraRoles, JSON.stringify(nextExtraRoles));
          if (nextDisplayName) {
            sessionStorage.setItem(AUTH_SESSION_KEYS.displayName, nextDisplayName);
          } else {
            sessionStorage.removeItem(AUTH_SESSION_KEYS.displayName);
          }
          if (nextAvatarUrl) {
            sessionStorage.setItem(AUTH_SESSION_KEYS.avatarUrl, nextAvatarUrl);
          } else {
            sessionStorage.removeItem(AUTH_SESSION_KEYS.avatarUrl);
          }
          setIsAuthLoading(false);
          return;
        }

        sessionStorage.removeItem(AUTH_SESSION_KEYS.token);
        sessionStorage.removeItem(AUTH_SESSION_KEYS.userId);
        sessionStorage.removeItem(AUTH_SESSION_KEYS.role);
        sessionStorage.removeItem(AUTH_SESSION_KEYS.extraRoles);
        sessionStorage.removeItem(AUTH_SESSION_KEYS.displayName);
        sessionStorage.removeItem(AUTH_SESSION_KEYS.avatarUrl);
        setIsAuthenticated(false);
        setUserId(null);
        setAdminId(null);
        setRole(null);
        setExtraRoles([]);
        setDisplayName(null);
        setAvatarUrl(null);
        setToken(null);
        setIsAuthLoading(false);
      } catch (e) {
        sessionStorage.removeItem(AUTH_SESSION_KEYS.token);
        sessionStorage.removeItem(AUTH_SESSION_KEYS.userId);
        sessionStorage.removeItem(AUTH_SESSION_KEYS.role);
        sessionStorage.removeItem(AUTH_SESSION_KEYS.extraRoles);
        sessionStorage.removeItem(AUTH_SESSION_KEYS.displayName);
        sessionStorage.removeItem(AUTH_SESSION_KEYS.avatarUrl);
        setIsAuthenticated(false);
        setUserId(null);
        setAdminId(null);
        setRole(null);
        setExtraRoles([]);
        setDisplayName(null);
        setAvatarUrl(null);
        setToken(null);
        setIsAuthLoading(false);
      }
    };

    hydrateAuth();
  }, []);

  const setAuth = (token: string, role: string, nextDisplayName?: string, nextAvatarUrl?: string | null, nextUserId?: number | null, nextExtraRoles?: string[], nextAdminId?: number | null): void => {
    clearLegacyAuthStorage();
    sessionStorage.setItem(AUTH_SESSION_KEYS.token, token);
    sessionStorage.setItem(AUTH_SESSION_KEYS.role, role);
    sessionStorage.setItem(AUTH_SESSION_KEYS.extraRoles, JSON.stringify(nextExtraRoles || []));
    if (nextUserId != null) {
      sessionStorage.setItem(AUTH_SESSION_KEYS.userId, String(nextUserId));
    } else {
      sessionStorage.removeItem(AUTH_SESSION_KEYS.userId);
    }
    if (nextAdminId != null) {
      sessionStorage.setItem(AUTH_SESSION_KEYS.adminId, String(nextAdminId));
    } else {
      sessionStorage.removeItem(AUTH_SESSION_KEYS.adminId);
    }
    if (nextDisplayName) {
      sessionStorage.setItem(AUTH_SESSION_KEYS.displayName, nextDisplayName);
    } else {
      sessionStorage.removeItem(AUTH_SESSION_KEYS.displayName);
    }
    if (nextAvatarUrl) {
      sessionStorage.setItem(AUTH_SESSION_KEYS.avatarUrl, nextAvatarUrl);
    } else {
      sessionStorage.removeItem(AUTH_SESSION_KEYS.avatarUrl);
    }
    setIsAuthLoading(false);
    setIsAuthenticated(true);
    setUserId(nextUserId ?? null);
    setAdminId(nextAdminId ?? null);
    setToken(token);
    setRole(role);
    setExtraRoles(nextExtraRoles || []);
    setDisplayName(nextDisplayName || null);
    setAvatarUrl(nextAvatarUrl || null);
  };

  const logout = (): void => {
    const baseUrl = getConfig().API_BASE_URL;
    fetch(`${baseUrl}/api/user/logout`, {
      method: 'POST',
      credentials: 'include',
    });

    clearLegacyAuthStorage();
    sessionStorage.removeItem(AUTH_SESSION_KEYS.token);
    sessionStorage.removeItem(AUTH_SESSION_KEYS.userId);
    sessionStorage.removeItem(AUTH_SESSION_KEYS.adminId);
    sessionStorage.removeItem(AUTH_SESSION_KEYS.role);
    sessionStorage.removeItem(AUTH_SESSION_KEYS.extraRoles);
    sessionStorage.removeItem(AUTH_SESSION_KEYS.displayName);
    sessionStorage.removeItem(AUTH_SESSION_KEYS.avatarUrl);
    setIsAuthLoading(false);
    setIsAuthenticated(false);
    setUserId(null);
    setAdminId(null);
    setToken(null);
    setRole(null);
    setExtraRoles([]);
    setDisplayName(null);
    setAvatarUrl(null);
  };

  const hasRole = (roleName: string): boolean => {
    return role === roleName || extraRoles.includes(roleName);
  };

  const getToken = (): string | null => token;

  const refreshAuth = async (): Promise<void> => {
    try {
      const baseUrl = getConfig().API_BASE_URL;
      const sessionToken = sessionStorage.getItem(AUTH_SESSION_KEYS.token);
      const response = await fetch(`${baseUrl}/api/account`, {
        method: 'GET',
        credentials: 'include',
        headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : undefined,
      });
      if (!response.ok) return;
      const payload = await response.json();
      if (payload?.EC === 0 && payload?.DT?.access_token) {
        const d = payload.DT;
        const nextDisplayName = d.username || null;
        const nextAvatarUrl = d.avatarUrl || null;
        setDisplayName(nextDisplayName);
        setAvatarUrl(nextAvatarUrl);
        if (nextDisplayName) sessionStorage.setItem(AUTH_SESSION_KEYS.displayName, nextDisplayName);
        else sessionStorage.removeItem(AUTH_SESSION_KEYS.displayName);
        if (nextAvatarUrl) sessionStorage.setItem(AUTH_SESSION_KEYS.avatarUrl, nextAvatarUrl);
        else sessionStorage.removeItem(AUTH_SESSION_KEYS.avatarUrl);
      }
    } catch {
      // silent — don't break the UI
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isAuthLoading, userId, adminId, role, extraRoles, displayName, avatarUrl, setAuth, hasRole, logout, getToken, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
